/* eslint-disable no-param-reassign,id-length, perfectionist/sort-objects */
import { clamp } from '@deltasierra/utilities-math';
import {
    ColorScheme,
    ColorSchemeHelper,
    RGB,
    parseDegree,
    parseOpacity,
    parsePercent,
    RGBHelper,
    sanitiseDegree,
    sanitiseOpacity,
    sanitisePercent,
} from './common';

/**
 * Represents a color in HSL color space.
 * Hue is expressed as an integer between 0 and 360.
 * Saturation is express as a decimal between 0 and 1.
 * Luminance is expressed as a decimal between 0 and 1.
 * Luminance range is like a gradient overlay that varies
 * from 0 (black and fully opaque)
 * to .5 (fully transparent)
 * to 1 (white and fully opaque).
 * If you find Luminance confusing, you may find the HSV scheme
 * more intuitive - see hsv.ts for more.
 */
export interface HSL {
    h: number;
    s: number;
    l: number;
    a?: number;
}

function hue2rgb(p: number, q: number, t: number) {
    if (t < 0) {
        t += 1;
    }
    if (t > 1) {
        t -= 1;
    }
    if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
    }
    if (t < 1 / 2) {
        return q;
    }
    if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
    }

    return p;
}

export class HSLHelper implements ColorSchemeHelper<HSL> {
    public scheme: ColorScheme = 'hsl';

    private rgbHelper = new RGBHelper();

    public isMatch(color: object): color is HSL {
        return 'h' in color && 's' in color && 'l' in color;
    }

    public sanitise(color: HSL): HSL {
        return {
            h: sanitiseDegree(color.h),
            s: sanitisePercent(color.s),
            l: sanitisePercent(color.l),
            a: sanitiseOpacity(color.a),
        };
    }

    public parse(color: string): HSL | undefined {
        const patterns = [
            /^hsl\(\s*((?:\d+(?:\.\d+)?)|\.\d+)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%)\s*\)$/i,

            /^hsla\(\s*((?:\d+(?:\.\d+)?)|\.\d+)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%)\s*(?:,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)?))\s*\)$/i,
        ];

        const match = patterns.map(pattern => pattern.exec(color)).filter(m => !!m)[0];

        if (!match) {
            return undefined;
        }

        const result = this.sanitise({
            h: parseDegree(match[1]),
            s: parsePercent(match[2]),
            l: parsePercent(match[3]),
            a: parseOpacity(match[4]),
        });

        return result;
    }

    public toString(color: HSL): string {
        // eslint-disable-next-line prefer-const
        let { a, h, l, s } = color;

        h = clamp(0, 360, Math.round(h));
        s = clamp(0, 100, Math.round(s * 1000) / 10);
        l = clamp(0, 100, Math.round(l * 1000) / 10);

        if (a === 1 || a === undefined) {
            return `hsl(${h}, ${s}%, ${l}%)`;
        }

        return `hsla(${h}, ${s}%, ${l}%, ${a})`;
    }

    public fromRgb(rgb: RGB): HSL {
        // eslint-disable-next-line prefer-const
        let { a, b, g, r } = rgb;

        r /= 255;
        g /= 255;
        b /= 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0;
        let s: number;
        const l = (max + min) / 2;

        if (max === min) {
            h = s = 0; // Achromatic
        } else {
            const d = max - min;

            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;

                case g:
                    h = (b - r) / d + 2;
                    break;

                case b:
                    h = (r - g) / d + 4;
                    break;

                default:
                    break;
            }

            h /= 6;
        }

        h *= 360;

        const result = this.sanitise({ h, s, l, a });

        return result;
    }

    public toRgb(hsl: HSL, options?: { round?: boolean }): RGB {
        // eslint-disable-next-line prefer-const
        let { a, h, l, s } = hsl;

        h /= 360;

        let r;
        let g;
        let b;

        if (s === 0) {
            // Achromatic

            r = g = b = l;
        } else {
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }

        let round = true;
        if (options && options.round !== undefined) {
            round = options.round;
        }

        r *= 255;
        g *= 255;
        b *= 255;

        const result = this.rgbHelper.sanitise({ r, g, b, a }, round);

        return result;
    }

    public areEqual(colorA: HSL, colorB: HSL): boolean {
        const aIsNull = colorA === null;

        const bIsNull = colorB === null;

        if (aIsNull || bIsNull) {
            return aIsNull === bIsNull;
        }

        const aIsOpaque = colorA.a === 1 || colorA.a === undefined;
        const bIsOpaque = colorB.a === 1 || colorB.a === undefined;

        const areEqual =
            colorA.h === colorB.h &&
            colorA.s === colorB.s &&
            colorA.l === colorB.l &&
            (colorA.a === colorB.a || aIsOpaque === bIsOpaque);

        return areEqual;
    }
}
